import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { format } from "date-fns";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  Target,
  CheckCircle2,
  Clock,
  AlertTriangle,
  TrendingUp,
  Calendar,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface Goal {
  id: string;
  care_plan_id: string;
  title: string;
  description: string | null;
  target_value: number | null;
  current_value: number | null;
  unit: string | null;
  priority: "low" | "medium" | "high";
  status: "not_started" | "in_progress" | "achieved" | "discontinued";
  target_date: string | null;
  created_at: string;
}

interface GoalCardProps {
  goal: Goal;
  onUpdate: () => void;
}

const statusConfig = {
  not_started: { label: "Not Started", color: "bg-muted text-muted-foreground", icon: Clock },
  in_progress: { label: "In Progress", color: "bg-warning/10 text-warning", icon: TrendingUp },
  achieved: { label: "Achieved", color: "bg-success/10 text-success", icon: CheckCircle2 },
  discontinued: { label: "Discontinued", color: "bg-destructive/10 text-destructive", icon: AlertTriangle },
};

const priorityConfig = {
  low: { label: "Low", color: "bg-muted text-muted-foreground" },
  medium: { label: "Medium", color: "bg-warning/10 text-warning" },
  high: { label: "High", color: "bg-destructive/10 text-destructive" },
};

export function GoalCard({ goal, onUpdate }: GoalCardProps) {
  const { toast } = useToast();
  const [expanded, setExpanded] = useState(false);
  const [editingProgress, setEditingProgress] = useState(false);
  const [newValue, setNewValue] = useState(goal.current_value?.toString() || "0");

  const StatusIcon = statusConfig[goal.status].icon;

  const progress = goal.target_value
    ? Math.min(Math.round(((goal.current_value || 0) / goal.target_value) * 100), 100)
    : 0;

  const updateGoalMutation = useMutation({
    mutationFn: async (updates: Partial<Goal>) => {
      const { error } = await supabase
        .from("care_plan_goals")
        .update(updates)
        .eq("id", goal.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Goal updated" });
      onUpdate();
      setEditingProgress(false);
    },
    onError: (error) => {
      toast({ title: "Error updating goal", description: error.message, variant: "destructive" });
    },
  });

  const handleProgressUpdate = () => {
    const value = parseFloat(newValue);
    if (isNaN(value)) return;

    const updates: Partial<Goal> = { current_value: value };
    
    // Auto-update status based on progress
    if (goal.target_value && value >= goal.target_value) {
      updates.status = "achieved";
    } else if (value > 0 && goal.status === "not_started") {
      updates.status = "in_progress";
    }

    updateGoalMutation.mutate(updates);
  };

  return (
    <Card className={cn(
      "transition-all",
      goal.status === "achieved" && "border-success/50 bg-success/5"
    )}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-semibold">{goal.title}</h4>
              <Badge className={priorityConfig[goal.priority].color} variant="outline">
                {priorityConfig[goal.priority].label}
              </Badge>
            </div>
            <Badge className={statusConfig[goal.status].color}>
              <StatusIcon className="mr-1 h-3 w-3" />
              {statusConfig[goal.status].label}
            </Badge>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </div>

        {/* Progress Bar (if measurable goal) */}
        {goal.target_value && (
          <div className="mt-3">
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-muted-foreground">Progress</span>
              <span className="font-medium">
                {goal.current_value || 0} / {goal.target_value} {goal.unit}
              </span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        )}

        {/* Expanded Content */}
        {expanded && (
          <div className="mt-4 space-y-4 border-t pt-4">
            {goal.description && (
              <p className="text-sm text-muted-foreground">{goal.description}</p>
            )}

            {goal.target_date && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" />
                Target: {format(new Date(goal.target_date), "MMM d, yyyy")}
              </div>
            )}

            {/* Update Progress */}
            {goal.target_value && goal.status !== "achieved" && goal.status !== "discontinued" && (
              <div className="space-y-2">
                <p className="text-sm font-medium">Update Progress</p>
                {editingProgress ? (
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      value={newValue}
                      onChange={(e) => setNewValue(e.target.value)}
                      className="w-24"
                    />
                    <span className="self-center text-sm text-muted-foreground">{goal.unit}</span>
                    <Button
                      size="sm"
                      onClick={handleProgressUpdate}
                      disabled={updateGoalMutation.isPending}
                    >
                      Save
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setEditingProgress(false);
                        setNewValue(goal.current_value?.toString() || "0");
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingProgress(true)}
                  >
                    <TrendingUp className="mr-2 h-4 w-4" />
                    Log Progress
                  </Button>
                )}
              </div>
            )}

            {/* Status Actions */}
            {goal.status !== "achieved" && goal.status !== "discontinued" && (
              <div className="flex gap-2">
                {goal.status === "not_started" && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => updateGoalMutation.mutate({ status: "in_progress" })}
                    disabled={updateGoalMutation.isPending}
                  >
                    Start Goal
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="text-success"
                  onClick={() => updateGoalMutation.mutate({ status: "achieved" })}
                  disabled={updateGoalMutation.isPending}
                >
                  <CheckCircle2 className="mr-1 h-4 w-4" />
                  Mark Achieved
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
